/**
 * Admin DLQ (Dead Letter Queue) Dashboard Tab Component
 * 
 * Displays detailed information about failed execution jobs in the DLQ.
 */

import { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { getDLQMetrics, getDLQEntries, retryDLQEntry, deleteDLQEntry, deleteAllDLQEntries, type DLQMetrics, type DLQEntry } from "@app/services/adminDlqApi";
import { logger } from "@app/services/logger";
import "./admin-dlq.css";

const DLQ_METRICS_QUERY_KEY = ["admin", "dlq", "metrics"];
const DLQ_ENTRIES_QUERY_KEY = ["admin", "dlq", "entries"];

// Error explanations mapping
const ERROR_EXPLANATIONS: Record<string, string> = {
  INVALID_KEY: "Invalid API key - API credentials are invalid or expired",
  INSUFFICIENT_BALANCE: "Insufficient balance - Not enough funds to execute order",
  SUBSCRIPTION_INACTIVE: "Subscription inactive - User subscription has expired",
  USER_DISABLED: "User disabled - User account is disabled",
  USER_TRADING_DISABLED: "Trading disabled - Trading is disabled for this user",
  RATE_LIMIT: "Rate limit exceeded - Too many requests to exchange",
  TIMEOUT: "Request timeout - Network or exchange timeout",
  EXCHANGE_ERROR: "Exchange error - Exchange API returned an error",
  VALIDATION_ERROR: "Validation error - Order validation failed",
  CAPITAL_CHECK_FAILED: "Capital check failed - Insufficient capital",
  INTEGRATION_MISSING: "Integration missing - No Bybit integration found",
  DAILY_CAP_REACHED: "Daily cap reached - User reached daily trading limit",
  OTHER: "Other error - Unknown or unexpected error",
};

// Get error explanation
function getErrorExplanation(errorCategory: string | null, errorCode: string | null): string {
  if (errorCode && ERROR_EXPLANATIONS[errorCode]) {
    return ERROR_EXPLANATIONS[errorCode];
  }
  if (errorCategory && ERROR_EXPLANATIONS[errorCategory]) {
    return ERROR_EXPLANATIONS[errorCategory];
  }
  return ERROR_EXPLANATIONS.OTHER;
}

// Get problem type for categorization
function getProblemType(errorCategory: string | null, errorCode: string | null): "user" | "system" | "capital" | "config" {
  const code = errorCode || errorCategory || "";
  if (code.includes("INVALID_KEY") || code.includes("USER_") || code.includes("SUBSCRIPTION") || code.includes("INTEGRATION")) {
    return "user";
  }
  if (code.includes("BALANCE") || code.includes("CAPITAL")) {
    return "capital";
  }
  if (code.includes("RATE_LIMIT") || code.includes("TIMEOUT") || code.includes("EXCHANGE")) {
    return "system";
  }
  return "config";
}

interface DLQEntryRowProps {
  entry: DLQEntry;
  onRetry: (entryId: string) => void;
  onDelete: (entryId: string) => void;
  isSelected: boolean;
  onSelect: (entryId: string, selected: boolean) => void;
}

function DLQEntryRow({ entry, onRetry, onDelete, isSelected, onSelect }: DLQEntryRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const problemType = getProblemType(entry.error_category, entry.last_error_code);
  const errorExplanation = getErrorExplanation(entry.error_category, entry.last_error_code);

  return (
    <>
      <tr 
        key={entry.job_id} 
        className={`admin-dlq-table__row ${isExpanded ? "admin-dlq-table__row--expanded" : ""} ${isSelected ? "admin-dlq-table__row--selected" : ""}`}
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ cursor: "pointer" }}
      >
        <td className="admin-dlq-table__cell" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => onSelect(entry.job_id, e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            style={{ cursor: "pointer" }}
          />
        </td>
        <td className="admin-dlq-table__cell">
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span style={{ fontSize: "0.875rem" }}>{isExpanded ? "▼" : "▶"}</span>
            <span className="admin-dlq-table__cell--mono" style={{ fontSize: "0.8rem" }}>
              {entry.job_id.substring(0, 12)}...
            </span>
          </div>
        </td>
        <td className="admin-dlq-table__cell">
          <span className={`admin-dlq-badge admin-dlq-badge--${entry.job_type.toLowerCase()}`}>
            {entry.job_type}
          </span>
        </td>
        <td className="admin-dlq-table__cell">
          {entry.user_info ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
              <span style={{ fontWeight: 500 }}>{entry.user_info.name}</span>
              <span style={{ fontSize: "0.75rem", color: "#666" }}>{entry.user_info.email}</span>
            </div>
          ) : (
            <span className="admin-dlq-table__cell--mono" style={{ fontSize: "0.8rem" }}>
              {entry.user_id.substring(0, 8)}...
            </span>
          )}
        </td>
        <td className="admin-dlq-table__cell">
          {entry.symbol ? (
            <span style={{ fontWeight: 500 }}>{entry.symbol}</span>
          ) : (
            <span style={{ color: "#999" }}>-</span>
          )}
        </td>
        <td className="admin-dlq-table__cell">
          <span className={`admin-dlq-badge admin-dlq-badge--problem admin-dlq-badge--problem-${problemType}`}>
            {problemType}
          </span>
        </td>
        <td className="admin-dlq-table__cell">
          <span className={`admin-dlq-badge admin-dlq-badge--error admin-dlq-badge--${entry.error_category?.toLowerCase() || "unknown"}`}>
            {entry.error_category || "UNKNOWN"}
          </span>
        </td>
        <td className="admin-dlq-table__cell admin-dlq-table__cell--error">
          <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>{errorExplanation}</span>
            {entry.last_error && (
              <span style={{ fontSize: "0.75rem", color: "#666" }} title={entry.last_error}>
                {entry.last_error.length > 60 ? `${entry.last_error.substring(0, 60)}...` : entry.last_error}
              </span>
            )}
          </div>
        </td>
        <td className="admin-dlq-table__cell">
          <span className={entry.attempts >= entry.max_attempts ? "admin-dlq-attempts--max" : ""}>
            {entry.attempts}/{entry.max_attempts}
          </span>
        </td>
        <td className="admin-dlq-table__cell admin-dlq-table__cell--mono">
          {entry.created_at ? new Date(entry.created_at).toLocaleString() : "-"}
        </td>
        <td className="admin-dlq-table__cell" onClick={(e) => e.stopPropagation()}>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              type="button"
              onClick={() => onRetry(entry.job_id)}
              className="admin-dlq-action-button admin-dlq-action-button--retry"
              title="Retry job"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={() => onDelete(entry.job_id)}
              className="admin-dlq-action-button admin-dlq-action-button--delete"
              title="Delete entry"
            >
              Delete
            </button>
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr className="admin-dlq-table__row admin-dlq-table__row--details">
          <td colSpan={11} className="admin-dlq-table__cell admin-dlq-table__cell--details">
            <div className="admin-dlq-entry-details">
              <div className="admin-dlq-entry-details__section">
                <h4>Full Details</h4>
                <div className="admin-dlq-entry-details__grid">
                  <div>
                    <strong>Job ID:</strong> <code>{entry.job_id}</code>
                  </div>
                  <div>
                    <strong>User ID:</strong> <code>{entry.user_id}</code>
                  </div>
                  {entry.signal_id && (
                    <div>
                      <strong>Signal ID:</strong> <code>{entry.signal_id}</code>
                    </div>
                  )}
                  {entry.position_id && (
                    <div>
                      <strong>Position ID:</strong> <code>{entry.position_id}</code>
                    </div>
                  )}
                  {entry.recommendation_id && (
                    <div>
                      <strong>Recommendation ID:</strong> <code>{entry.recommendation_id}</code>
                    </div>
                  )}
                  <div>
                    <strong>Created:</strong> {entry.created_at ? new Date(entry.created_at).toLocaleString() : "-"}
                  </div>
                  <div>
                    <strong>Completed:</strong> {entry.completed_at ? new Date(entry.completed_at).toLocaleString() : "-"}
                  </div>
                </div>
              </div>
              
              {entry.dead_letter_reason && (
                <div className="admin-dlq-entry-details__section">
                  <h4>Dead Letter Reason</h4>
                  <p>{entry.dead_letter_reason}</p>
                </div>
              )}
              
              {entry.last_error_message && (
                <div className="admin-dlq-entry-details__section">
                  <h4>Error Message</h4>
                  <pre className="admin-dlq-entry-details__error">{entry.last_error_message}</pre>
                </div>
              )}
              
              {entry.job_payload && (
                <div className="admin-dlq-entry-details__section">
                  <h4>Job Payload</h4>
                  <pre className="admin-dlq-entry-details__payload">
                    {JSON.stringify(entry.job_payload, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function AdminDLQTab(): JSX.Element {
  const queryClient = useQueryClient();
  const [entriesLimit] = useState(100);
  const [entriesOffset, setEntriesOffset] = useState(0);
  const [retryingIds, setRetryingIds] = useState<Set<string>>(new Set());
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [selectedEntries, setSelectedEntries] = useState<Set<string>>(new Set());
  const [isDeletingAll, setIsDeletingAll] = useState(false);

  // Fetch DLQ metrics
  const { data: metrics, isLoading: isLoadingMetrics, isError: isMetricsError } = useQuery<DLQMetrics>({
    queryKey: DLQ_METRICS_QUERY_KEY,
    queryFn: getDLQMetrics,
    refetchInterval: 30000,
  });

  // Fetch DLQ entries
  const { data: entriesData, isLoading: isLoadingEntries, isError: isEntriesError } = useQuery({
    queryKey: [...DLQ_ENTRIES_QUERY_KEY, entriesLimit, entriesOffset],
    queryFn: () => getDLQEntries(entriesLimit, entriesOffset),
    refetchInterval: 60000,
  });

  const handleRetry = async (entryId: string) => {
    if (retryingIds.has(entryId)) return;
    
    setRetryingIds((prev) => new Set(prev).add(entryId));
    try {
      await retryDLQEntry(entryId);
      await queryClient.invalidateQueries({ queryKey: DLQ_ENTRIES_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: DLQ_METRICS_QUERY_KEY });
    } catch (error: any) {
      logger.error("Error retrying DLQ entry:", error);
      alert(`Failed to retry entry: ${error.message}`);
    } finally {
      setRetryingIds((prev) => {
        const next = new Set(prev);
        next.delete(entryId);
        return next;
      });
    }
  };

  const handleDelete = async (entryId: string) => {
    if (!confirm("Are you sure you want to delete this DLQ entry?")) return;
    if (deletingIds.has(entryId)) return;
    
    setDeletingIds((prev) => new Set(prev).add(entryId));
    try {
      await deleteDLQEntry(entryId);
      setSelectedEntries((prev) => {
        const next = new Set(prev);
        next.delete(entryId);
        return next;
      });
      await queryClient.invalidateQueries({ queryKey: DLQ_ENTRIES_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: DLQ_METRICS_QUERY_KEY });
    } catch (error: any) {
      logger.error("Error deleting DLQ entry:", error);
      alert(`Failed to delete entry: ${error.message}`);
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(entryId);
        return next;
      });
    }
  };

  const handleSelectEntry = (entryId: string, selected: boolean) => {
    setSelectedEntries((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(entryId);
      } else {
        next.delete(entryId);
      }
      return next;
    });
  };

  // CRITICAL: All hooks must be called before any conditional returns
  // Move useRef and useEffect here to avoid React Hooks order violation
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);
  const entries = entriesData?.entries || [];
  const totalEntries = entriesData?.total_count || entriesData?.count || 0;
  const isAllSelected = entries.length > 0 && selectedEntries.size === entries.length;
  const isSomeSelected = selectedEntries.size > 0 && selectedEntries.size < entries.length;

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = isSomeSelected;
    }
  }, [isSomeSelected]);

  const isLoading = isLoadingMetrics || isLoadingEntries;
  const isError = isMetricsError || isEntriesError;

  if (isLoading) {
    return (
      <div className="admin-dlq-root">
        <Card title="Dead Letter Queue (DLQ)">
          <div className="admin-dlq-loading">Loading DLQ data...</div>
        </Card>
      </div>
    );
  }

  if (isError || !metrics) {
    return (
      <div className="admin-dlq-root">
        <Card title="Dead Letter Queue (DLQ)">
          <div className="admin-dlq-error">Failed to load DLQ data.</div>
        </Card>
      </div>
    );
  }

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      const allIds = new Set(entries.map((e) => e.job_id));
      setSelectedEntries(allIds);
    } else {
      setSelectedEntries(new Set());
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedEntries.size === 0) {
      alert("No entries selected");
      return;
    }

    const count = selectedEntries.size;
    if (!confirm(`Are you sure you want to delete ${count} DLQ ${count === 1 ? "entry" : "entries"}?`)) {
      return;
    }

    const entriesToDelete = Array.from(selectedEntries);
    const errors: string[] = [];
    let successCount = 0;

    // Add all to deletingIds
    setDeletingIds((prev) => {
      const next = new Set(prev);
      entriesToDelete.forEach((id) => next.add(id));
      return next;
    });

    // Delete entries sequentially to avoid overwhelming the server
    for (const entryId of entriesToDelete) {
      try {
        await deleteDLQEntry(entryId);
        successCount++;
        setSelectedEntries((prev) => {
          const next = new Set(prev);
          next.delete(entryId);
          return next;
        });
      } catch (error: any) {
        logger.error(`Error deleting DLQ entry ${entryId}:`, error);
        errors.push(`${entryId}: ${error.message || "Unknown error"}`);
      }
    }

    // Remove all from deletingIds
    setDeletingIds((prev) => {
      const next = new Set(prev);
      entriesToDelete.forEach((id) => next.delete(id));
      return next;
    });

    // Refresh queries
    await queryClient.invalidateQueries({ queryKey: DLQ_ENTRIES_QUERY_KEY });
    await queryClient.invalidateQueries({ queryKey: DLQ_METRICS_QUERY_KEY });

    // Show results
    if (errors.length > 0) {
      alert(
        `Deleted ${successCount} of ${count} entries.\n\nErrors:\n${errors.slice(0, 5).join("\n")}${errors.length > 5 ? `\n... and ${errors.length - 5} more` : ""}`
      );
    } else {
      alert(`Successfully deleted ${successCount} ${successCount === 1 ? "entry" : "entries"}`);
    }
  };

  const handleDeleteAll = async () => {
    const totalCount = totalEntries;
    if (totalCount === 0) {
      alert("No DLQ entries to delete");
      return;
    }

    if (!confirm(`Are you sure you want to delete ALL ${totalCount} DLQ entries?\n\nThis action cannot be undone!`)) {
      return;
    }

    setIsDeletingAll(true);
    try {
      const result = await deleteAllDLQEntries();
      
      // Clear selected entries
      setSelectedEntries(new Set());
      
      // Refresh queries
      await queryClient.invalidateQueries({ queryKey: DLQ_ENTRIES_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: DLQ_METRICS_QUERY_KEY });
      
      // Show results
      const message = `Successfully deleted ${result.deleted_from_db} entries from database` +
        (result.deleted_from_redis > 0 ? ` and ${result.deleted_from_redis} from Redis stream` : "") +
        (result.errors.length > 0 ? `\n\nErrors: ${result.errors.join(", ")}` : "");
      alert(message);
    } catch (error: any) {
      logger.error("Error deleting all DLQ entries:", error);
      alert(`Failed to delete all DLQ entries: ${error.message || "Unknown error"}`);
    } finally {
      setIsDeletingAll(false);
    }
  };

  return (
    <div className="admin-dlq-root">
      <Card title="Dead Letter Queue (DLQ)" subtitle="Failed execution jobs that exceeded max retry attempts">
        {/* Metrics Section */}
        <div className="admin-dlq-metrics">
          <div className="admin-dlq-metric-card">
            <h3>DLQ Overview</h3>
            <div className="admin-dlq-metric-grid">
              <div className="admin-dlq-metric">
                <span className="admin-dlq-metric__label">Total Entries</span>
                <span className={`admin-dlq-metric__value ${metrics.spike_detected ? "admin-dlq-metric__value--warning" : ""}`}>
                  {metrics.dlq_length}
                </span>
              </div>
              <div className="admin-dlq-metric">
                <span className="admin-dlq-metric__label">Recent (5 min)</span>
                <span className={`admin-dlq-metric__value ${metrics.recent_entries_count >= metrics.spike_threshold ? "admin-dlq-metric__value--error" : ""}`}>
                  {metrics.recent_entries_count}
                </span>
              </div>
              <div className="admin-dlq-metric">
                <span className="admin-dlq-metric__label">Spike Threshold</span>
                <span className="admin-dlq-metric__value">{metrics.spike_threshold}</span>
              </div>
              <div className="admin-dlq-metric">
                <span className="admin-dlq-metric__label">Status</span>
                <span className={`admin-dlq-metric__value ${metrics.spike_detected ? "admin-dlq-metric__value--error" : "admin-dlq-metric__value--success"}`}>
                  {metrics.spike_detected ? "⚠️ Spike Detected" : "✓ Normal"}
                </span>
              </div>
            </div>
          </div>

          {/* Error Categories */}
          <div className="admin-dlq-metric-card">
            <h3>Error Categories</h3>
            {Object.keys(metrics.error_categories).length > 0 ? (
              <div className="admin-dlq-error-categories">
                {Object.entries(metrics.error_categories)
                  .sort(([, a], [, b]) => b - a)
                  .map(([category, count]) => (
                    <div key={category} className="admin-dlq-error-category">
                      <span className="admin-dlq-error-category__name">{category || "UNKNOWN"}</span>
                      <span className="admin-dlq-error-category__count">{count}</span>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="admin-dlq-empty" style={{ padding: "1rem", textAlign: "left" }}>
                No error categories found in recent entries.
              </div>
            )}
          </div>
        </div>

        {/* Entries Table */}
        <div className="admin-dlq-entries">
          <div className="admin-dlq-entries-header">
            <h3>Failed Jobs ({totalEntries})</h3>
            <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
              {totalEntries > 0 && (
                <button
                  type="button"
                  onClick={handleDeleteAll}
                  className="admin-dlq-action-button admin-dlq-action-button--delete"
                  disabled={isDeletingAll}
                  style={{ fontWeight: 600, backgroundColor: "#dc3545", color: "white" }}
                >
                  {isDeletingAll ? "Deleting All..." : `Delete All (${totalEntries})`}
                </button>
              )}
              {selectedEntries.size > 0 && (
                <button
                  type="button"
                  onClick={handleDeleteSelected}
                  className="admin-dlq-action-button admin-dlq-action-button--delete"
                  disabled={deletingIds.size > 0}
                  style={{ fontWeight: 600 }}
                >
                  {deletingIds.size > 0 ? "Deleting..." : `Delete Selected (${selectedEntries.size})`}
                </button>
              )}
              <div className="admin-dlq-pagination">
              <button
                type="button"
                onClick={() => setEntriesOffset(Math.max(0, entriesOffset - entriesLimit))}
                disabled={entriesOffset === 0}
                className="admin-dlq-pagination__button"
              >
                Previous
              </button>
              <span className="admin-dlq-pagination__info">
                Showing {entriesOffset + 1} - {Math.min(entriesOffset + entriesLimit, totalEntries)} of {totalEntries}
              </span>
              <button
                type="button"
                onClick={() => setEntriesOffset(entriesOffset + entriesLimit)}
                disabled={entriesOffset + entriesLimit >= totalEntries}
                className="admin-dlq-pagination__button"
              >
                Next
              </button>
            </div>
            </div>
          </div>

          {entries.length === 0 ? (
            <div className="admin-dlq-empty">No failed jobs found in DLQ.</div>
          ) : (
            <div className="admin-dlq-table-container">
              <table className="admin-dlq-table">
                <thead>
                  <tr>
                    <th style={{ width: "40px" }}>
                      <input
                        ref={selectAllCheckboxRef}
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        style={{ cursor: "pointer" }}
                      />
                    </th>
                    <th>Job ID</th>
                    <th>Type</th>
                    <th>User</th>
                    <th>Symbol</th>
                    <th>Problem</th>
                    <th>Error</th>
                    <th>Error Details</th>
                    <th>Attempts</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <DLQEntryRow
                      key={entry.job_id}
                      entry={entry}
                      onRetry={handleRetry}
                      onDelete={handleDelete}
                      isSelected={selectedEntries.has(entry.job_id)}
                      onSelect={handleSelectEntry}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
